import { ReactNode, useEffect, useId, useRef } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { ArcadeThemeColors } from '../../theme/arcadeColors'
import ArcadeButton from '../Arcade/Button/ArcadeButton'
import { Flex, FlexGrow, FullWidthFlex } from '../layout/Flex'
import Text from '../Text/Text'

const DescriptorText = styled(Text)`
  text-align: right;
`

const renderTitle = (content: ReactNode, ariaDescribedBy?: string): ReactNode => {
  if (typeof content === 'string') {
    return (
      <Text as="p" aria-describedby={ariaDescribedBy} typography="labelMedium" color="textStandard">
        {content}
      </Text>
    )
  }
  return content
}

const renderBody = (content: ReactNode, id?: string): ReactNode => {
  if (typeof content === 'string') {
    return (
      <Text id={id} as="p" typography="bodySmall">
        {content}
      </Text>
    )
  }
  return content
}

const renderValue = (content: ReactNode): ReactNode => {
  if (typeof content === 'string') {
    return (
      <Text as="p" typography="labelMedium" color="textStandard">
        {content}
      </Text>
    )
  }
  return content
}

const renderDescriptor = (content: ReactNode): ReactNode => {
  if (typeof content === 'string') {
    return (
      <DescriptorText as="p" typography="bodySmall">
        {content}
      </DescriptorText>
    )
  }
  return content
}

export type TimelineComponentEvent = {
  title: ReactNode
  status: EventStatus
  icon?: ReactNode
  body?: ReactNode
  value?: ReactNode
  descriptor?: ReactNode
  button?: { label: string; onClick: () => void }
  /** Makes the complete timeline row keyboard-activatable. */
  onClick?: () => void
  /** Optional accessible name for an interactive row. */
  ariaLabel?: string
  /** Renders the row text with a line-through, e.g. for a skipped step. */
  strikethrough?: boolean
  /** Visually hidden text announcing the row's state to assistive technology. */
  statusLabel?: string
  /** Moves keyboard focus to this row on mount, e.g. after revealing it. */
  autoFocus?: boolean
}

export type TimelineProps = {
  events: TimelineComponentEvent[]
}

type ArcadeThemeColorKeys = keyof ArcadeThemeColors['component'] | keyof ArcadeThemeColors['semantic']

type EventStatus = 'NOT_STARTED' | 'INCOMPLETE' | 'PENDING' | 'DONE' | 'BYPASSED' | 'FAILED' | 'ALERT'
type EventStatusIconVariant = {
  topLineColor: ArcadeThemeColorKeys
  circleBackground: ArcadeThemeColorKeys
  circleBorder: ArcadeThemeColorKeys
  bottomLineColor: ArcadeThemeColorKeys
}

const getArcadeColor = (theme: { arcadeColors: ArcadeThemeColors }, colorKey: ArcadeThemeColorKeys): string => {
  return (
    (theme.arcadeColors.component as Record<string, string>)[colorKey] ||
    (theme.arcadeColors.semantic as Record<string, string>)[colorKey]
  )
}

const EventContent = styled(FullWidthFlex)<{ $strikethrough?: boolean }>`
  width: 100%;
  ${({ theme: { spacing } }) => `padding: ${spacing.m} 0 ${spacing.m} ${spacing.l};`};
  ${({ $strikethrough }) => ($strikethrough ? 'text-decoration: line-through;' : '')};
`

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const TimelineContainer = styled(Flex)`
  min-width: 17rem;
`

const TimelineRowContainer = styled(Flex)<{ $interactive: boolean }>`
  position: relative;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`

// Keeps an inline action clickable above a full-row overlay button.
const InlineActionContainer = styled.div`
  position: relative;
  z-index: 2;
`

const TimelineRowAction = styled.button`
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  padding: 0;
  border: 0;
  margin: 0;
  cursor: pointer;
  background: transparent;

  &:focus-visible {
    border-radius: ${({ theme }) => theme.border.radius.medium};
    outline: 2px solid ${({ theme }) => theme.arcadeColors.semantic.borderProminent};
    outline-offset: ${({ theme }) => theme.spacing.xxs};
  }
`
const TimelineIconColorMap: Record<EventStatus, EventStatusIconVariant> = {
  NOT_STARTED: {
    topLineColor: 'timelineLineBorderNotStarted',
    circleBackground: 'timelineDotBackgroundNotStarted',
    circleBorder: 'timelineDotBackgroundNotStarted',
    bottomLineColor: 'timelineLineBorderNotStarted',
  },
  INCOMPLETE: {
    topLineColor: 'timelineLineBorderInProgress',
    circleBackground: 'timelineDotBackgroundInProgress',
    circleBorder: 'timelineDotBorderInProgress',
    bottomLineColor: 'timelineLineBorderNotStarted',
  },
  PENDING: {
    topLineColor: 'timelineLineBorderPending',
    circleBackground: 'timelineDotBackgroundPending',
    circleBorder: 'timelineDotBackgroundPending',
    bottomLineColor: 'timelineLineBorderPending',
  },
  DONE: {
    topLineColor: 'timelineLineBorderDone',
    circleBackground: 'timelineDotBackgroundDone',
    circleBorder: 'timelineDotBackgroundDone',
    bottomLineColor: 'timelineLineBorderDone',
  },
  BYPASSED: {
    topLineColor: 'timelineLineBorderSkipped',
    circleBackground: 'timelineDotBackgroundSkipped',
    circleBorder: 'timelineDotBackgroundSkipped',
    bottomLineColor: 'timelineLineBorderSkipped',
  },
  FAILED: {
    topLineColor: 'timelineLineBorderInProgress',
    circleBackground: 'iconDanger',
    circleBorder: 'iconDanger',
    bottomLineColor: 'timelineLineBorderNotStarted',
  },
  ALERT: {
    topLineColor: 'timelineLineBorderInProgress',
    circleBackground: 'iconDanger',
    circleBorder: 'iconDanger',
    bottomLineColor: 'timelineLineBorderNotStarted',
  },
}

const TopLine = styled.div<{ isInitialEvent: boolean; status: EventStatus; previousEventStatus: EventStatus }>`
  min-height: 1.125rem;
  min-width: 0.125rem;

  ${({ isInitialEvent, previousEventStatus, theme }) => {
    if (isInitialEvent) {
      return `visibility: hidden;`
    } else {
      const arcadeColor: ArcadeThemeColorKeys = TimelineIconColorMap[previousEventStatus].bottomLineColor
      const themeColor = getArcadeColor(theme, arcadeColor)
      return `
          visibility: visible;
          background: ${themeColor};
        `
    }
  }}
`

const Circle = styled.div<{ status: EventStatus }>`
  ${({ theme: { spacing } }) => `width: ${spacing.s};`};

  ${({ theme: { spacing } }) => `height: ${spacing.s};`};

  border-radius: ${({ theme }) => theme.border.radius.circle};
  background: ${({ status, theme }) => {
    const semanticColor = TimelineIconColorMap[status].circleBackground
    const themeColor = getArcadeColor(theme, semanticColor)
    return status === 'INCOMPLETE' ? 'transparent' : themeColor
  }};
  border: ${({ status, theme }) => {
    const semanticColor = TimelineIconColorMap[status].circleBorder
    const themeColor = getArcadeColor(theme, semanticColor)
    return `0.125rem solid ${themeColor}`
  }};
  ${({ theme: { spacing } }) => `margin: ${spacing.xxs} 0.125rem;`};
`

const Icon = styled.div<{ status: EventStatus }>`
  ${({ theme: { spacing } }) => `width: ${spacing.m};`};

  ${({ theme: { spacing } }) => `height: ${spacing.m};`};

  color: ${({ status, theme }) => {
    const semanticColor = TimelineIconColorMap[status].circleBackground
    const themeColor = getArcadeColor(theme, semanticColor)
    return status === 'INCOMPLETE' ? 'transparent' : themeColor
  }};
  ${({ theme: { spacing } }) => `margin: ${spacing.xxs} ${spacing.empty};`};
`

const BottomLine = styled(FlexGrow)<{ status: EventStatus; isFinalEvent: boolean }>`
  min-width: 0.125rem;

  ${({ isFinalEvent, status, theme }) => {
    if (isFinalEvent) {
      return `visibility: hidden;`
    } else {
      const semanticColor = TimelineIconColorMap[status].bottomLineColor
      const themeColor = getArcadeColor(theme, semanticColor)
      return `
          visibility: visible;
          background: ${themeColor};
        `
    }
  }};
`

export const TimelineRow = ({
  event,
  previousEventStatus,
  isInitialEvent,
  isFinalEvent,
}: {
  event: TimelineComponentEvent
  previousEventStatus: EventStatus
  isInitialEvent: boolean
  isFinalEvent: boolean
}) => {
  const bodyId = useId()
  const {
    title,
    status,
    body,
    value,
    descriptor,
    button,
    icon,
    onClick,
    ariaLabel,
    strikethrough,
    statusLabel,
    autoFocus,
  } = event
  const { spacing } = useTheme()
  // TimelineRowContainer is a styled div rendered `as="li"`, so its ref type
  // is div-based; the runtime element is still an <li> that can take focus.
  const rowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (autoFocus) rowRef.current?.focus()
  }, [autoFocus])

  // Fall back to a string title so an interactive row always has an
  // accessible name even when the caller omits ariaLabel.
  const rowActionLabel = ariaLabel ?? (typeof title === 'string' ? title : undefined)

  return (
    <TimelineRowContainer as="li" $interactive={Boolean(onClick)} ref={rowRef} tabIndex={autoFocus ? -1 : undefined}>
      {onClick && <TimelineRowAction aria-label={rowActionLabel} onClick={onClick} type="button" />}
      <Flex flexDirection="column" alignItems="center">
        <TopLine isInitialEvent={isInitialEvent} status={status} previousEventStatus={previousEventStatus} />
        {event.status === 'INCOMPLETE' ||
        event.status === 'PENDING' ||
        event.status === 'NOT_STARTED' ||
        event.status === 'ALERT' ||
        !icon ? (
          <Circle status={status} />
        ) : (
          <Icon status={status}>{icon}</Icon>
        )}
        <BottomLine isFinalEvent={isFinalEvent} status={status} />
      </Flex>
      <EventContent justifyContent="space-between" flexDirection="column" $strikethrough={strikethrough}>
        <Flex flexDirection="column" gap={spacing.s}>
          <Flex justifyContent="space-between">
            <div>
              {renderTitle(title, body ? bodyId : undefined)}
              {statusLabel && <VisuallyHidden>{statusLabel}</VisuallyHidden>}
              {body && renderBody(body, bodyId)}
            </div>
            <div>
              {value && renderValue(value)}
              {descriptor && renderDescriptor(descriptor)}
            </div>
          </Flex>
          {button ? (
            <InlineActionContainer>
              <ArcadeButton size="compact" prominence="standard" onClick={button.onClick}>
                {button.label}
              </ArcadeButton>
            </InlineActionContainer>
          ) : null}
        </Flex>
      </EventContent>
    </TimelineRowContainer>
  )
}

export const Timeline = ({ events }: TimelineProps) => {
  return (
    <TimelineContainer flexDirection="column" as="ol" data-testid="arcade-timeline">
      {events.map((event, index) => (
        <TimelineRow
          key={index}
          event={event}
          isInitialEvent={index === 0}
          isFinalEvent={index === events.length - 1}
          previousEventStatus={events[index - 1]?.status}
        />
      ))}
    </TimelineContainer>
  )
}

export default Timeline
